import { LineProbeRunner, LineProbeStatsQueue, type LoadLevel, type LoadStatus, type TargetStatus, type WindowSummary } from '@core/line-probe';
import { type BaseApiResponse, OptionsApi, settleApiCall } from '@core/swagger-client';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { loadKbps } from './loadLevels';
import { MAX_SECONDS, PerSecondSeries } from './perSecond';

/** Spec 9 cadence of the test suite: a probe per target every 10 ms. */
const INTERVAL_MS = 10;
/** After Stop, the queue gets one more flush this much later before it is dropped. */
const FINAL_RETRY_MS = 2_000;

export type Phase = 'idle' | 'starting' | 'running' | 'stopped';

/** A point in the run, in seconds since its start. */
export interface LoadStep {
    second: number;
    kbps: number;
}

export interface LevelMark {
    second: number;
    level: LoadLevel;
}

export interface LineQualityRun {
    series: PerSecondSeries;
    /** In the order of the grant; the first carries the load. */
    targets: string[];
    statuses: Record<string, TargetStatus>;
    loadStatus: LoadStatus;
    windows: WindowSummary[];
    /** The load rate actually aimed at: the chosen level's while the load runs, else 0. */
    loadSteps: LoadStep[];
    /** Level changes while running, drawn as vertical markers. */
    levelMarks: LevelMark[];
}

/** A grant without targets: the line probe has nothing to measure against here. */
const NO_TARGETS: BaseApiResponse = { success: false, statusCode: 409, errorCode: 'LineProbeDisabled' };

/**
 * Runs the line probe of the test suite (spec 7). The runner exists only between start and stop,
 * created in an effect, so never during server-side rendering; leaving the page stops it. Sealed
 * windows go to the Dashboard; a failure to get the first grant becomes `error`, anything after
 * that a status: the page never throws.
 */
export function useLineQuality() {
    const grantMutation = useMutation(OptionsApi.testSuite.lineProbeSession());
    const statsMutation = useMutation(OptionsApi.testSuite.lineProbeStats());
    // mutateAsync keeps working after the page is gone, so the windows flushed on unmount still arrive.
    const grantRef = useRef(grantMutation.mutateAsync);
    const statsRef = useRef(statsMutation.mutateAsync);
    grantRef.current = grantMutation.mutateAsync;
    statsRef.current = statsMutation.mutateAsync;

    const [active, setActive] = useState(false);
    const [phase, setPhase] = useState<Phase>('idle');
    const [error, setError] = useState<BaseApiResponse>();
    const [level, setLevelState] = useState<LoadLevel>('off');
    const [run, setRun] = useState<LineQualityRun>();
    const [now, setNow] = useState(0);

    const levelRef = useRef<LoadLevel>('off');
    const loadStatusRef = useRef<LoadStatus>({ state: 'off' });
    const runnerRef = useRef<LineProbeRunner>(undefined);
    const seriesRef = useRef<PerSecondSeries>(undefined);

    /** Records the rate the load aims at whenever the level or the load's state changes. */
    const recordLoadStep = () => {
        const series = seriesRef.current;
        if (!series) return;
        const l = levelRef.current;
        const kbps = l !== 'off' && loadStatusRef.current.state === 'running' ? loadKbps(l) : 0;
        const second = Math.max(0, series.secondOfMono(performance.now()));
        setRun((r) => {
            if (!r || r.loadSteps[r.loadSteps.length - 1]?.kbps === kbps) return r;
            return { ...r, loadSteps: [...r.loadSteps, { second, kbps }] };
        });
    };

    useEffect(() => {
        if (!active) return;
        const series = new PerSecondSeries(performance.now(), Date.now());
        seriesRef.current = series;
        loadStatusRef.current = { state: 'off' };
        setRun({ series, targets: [], statuses: {}, loadStatus: { state: 'off' }, windows: [], loadSteps: [{ second: 0, kbps: 0 }], levelMarks: [] });
        setNow(series.originMono);

        let loadTarget: string | undefined;
        let stopped = false;
        const queue = new LineProbeStatsQueue(
            // A refused batch (4xx) settles as such and is dropped; a lost one stays queued.
            (batch) => settleApiCall(statsRef.current(batch)),
            () => true,
            () => runner.sessionId
        );
        const runner = new LineProbeRunner({
            intervalMs: INTERVAL_MS,
            getGrant: async () => {
                const result = await settleApiCall(grantRef.current());
                if (!result.success) throw result;
                if (result.data.targets.length === 0) throw NO_TARGETS;
                if (!loadTarget) {
                    loadTarget = result.data.targets[0].name;
                    const targets = result.data.targets.map((t) => t.name);
                    setRun((r) => r && { ...r, targets });
                }
                return result.data;
            },
            onSummary: (w) => {
                queue.push(w);
                setRun((r) => r && { ...r, windows: [...r.windows.filter((x) => x.windowStart > w.windowStart - MAX_SECONDS * 1000), w] });
            },
            onStatus: (target, status) => setRun((r) => r && { ...r, statuses: { ...r.statuses, [target]: status } }),
            onLoadStatus: (status) => {
                loadStatusRef.current = status;
                setRun((r) => r && { ...r, loadStatus: status });
                recordLoadStep();
            },
            onResolved: (target, probes, pairs) => series.addResolved(target, probes, pairs),
            onStalled: (target, fromMono, toMono) => series.addStall(target, fromMono, toMono),
            // The runner hands the report over the moment it arrives: now is its arrival on the probes' clock.
            onLoadSample: (sample) => {
                if (loadTarget) series.addLoadSample(loadTarget, sample, performance.now());
            }
        });
        runnerRef.current = runner;
        const l = levelRef.current;
        if (l !== 'off') runner.setLoad({ level: l, kbps: loadKbps(l) });

        setPhase('starting');
        setError(undefined);
        runner.start().then(
            () => setPhase((p) => (p === 'starting' ? 'running' : p)),
            (e: unknown) => {
                // A grant that fails after the user stopped is no longer news.
                if (stopped) return;
                setError(e as BaseApiResponse);
                setPhase('idle');
                setActive(false);
            }
        );
        // Charts move once a second; the series itself fills at the probe rate.
        const timer = setInterval(() => setNow(performance.now()), 1000);

        return () => {
            stopped = true;
            clearInterval(timer);
            runner.stop();
            // Stopping flushes the last windows; one more try covers a 5xx on that flush, then the queue is let go.
            setTimeout(() => void queue.flush(), FINAL_RETRY_MS);
            runnerRef.current = undefined;
            // Stopping closes the load without a status of its own.
            loadStatusRef.current = { state: 'off' };
            setRun((r) => r && { ...r, loadStatus: { state: 'off' } });
            recordLoadStep();
            setNow(performance.now());
        };
    }, [active]);

    const start = () => {
        setPhase('starting');
        setActive(true);
    };

    const stop = () => {
        setActive(false);
        setPhase('stopped');
    };

    const setLevel = (next: LoadLevel) => {
        levelRef.current = next;
        setLevelState(next);
        const runner = runnerRef.current;
        const series = seriesRef.current;
        if (!runner || !series) return;
        runner.setLoad(next === 'off' ? null : { level: next, kbps: loadKbps(next) });
        const second = Math.max(0, series.secondOfMono(performance.now()));
        setRun((r) => r && { ...r, levelMarks: [...r.levelMarks, { second, level: next }] });
        recordLoadStep();
    };

    const dismissError = () => setError(undefined);

    return { phase, error, dismissError, level, setLevel, run, now, start, stop };
}
